import {
  Typography,
  Box,
  TextField,
  FormControlLabel,
  Button,
  Autocomplete,
  Checkbox,
  Dialog,
  DialogContent,
  Radio,
  RadioGroup,
  FormControl,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Link, useLocation, useNavigate } from "react-router-dom";
import HelpTooltip from "../../components/HelpTooltip/HelpTooltip";
import { useEffect, useState } from "react";
import { useTheme } from "../../context/ThemeContext";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { commonThemes } from "../../utils/themes/themes";
import { getAppTheme } from "../../config/resolve-app-brand";
import { ReactComponent as ArrowBack } from "../../assets/arrow_back.svg";
import useApiCall from "../../hooks/ApiCall";
import useApiCallDefault from "../../hooks/ApiCallDefault";
import { default_headers, getCookie, getHeaders } from "../../utils/api";
import LookupAbn from "../../components/LookupAbn/LookupAbn";
import { enqueueSnackbar } from "notistack";
import envConfig from "../../config";
import { createSessionForExternal } from "../../utils/common";
import { CircularProgress } from "@mui/material";
import { setCrossDomainCookie } from "../../utils/cookie-utils";
import { checkAndUpdateApiKeyExpiration } from "../../components/Dashboard/Dashboard";
import {
  buildSubscriberAgentPayload,
  getDefaultFyProjectDate,
  shouldShowRegisteredAgentDetails,
} from "./subscriberAgentType";

export const AustralianBusinessNumber = (ABN) => {
  if (!ABN) return true;
  ABN = ABN.replace(/\s+/g, "");
  ABN = ABN.replace(/\D/g, "");

  if (ABN.length !== 11) {
    return false;
  }

  if (isNaN(ABN)) {
    return false;
  }

  let T1 = (parseInt(ABN.substring(0, 1)) - 1) * 10;
  let T2 = parseInt(ABN.substring(1, 2)) * 1;
  let T3 = parseInt(ABN.substring(2, 3)) * 3;
  let T4 = parseInt(ABN.substring(3, 4)) * 5;
  let T5 = parseInt(ABN.substring(4, 5)) * 7;
  let T6 = parseInt(ABN.substring(5, 6)) * 9;
  let T7 = parseInt(ABN.substring(6, 7)) * 11;
  let T8 = parseInt(ABN.substring(7, 8)) * 13;
  let T9 = parseInt(ABN.substring(8, 9)) * 15;
  let T10 = parseInt(ABN.substring(9, 10)) * 17;
  let T11 = parseInt(ABN.substring(10, 11)) * 19;

  let Chk = T1 + T2 + T3 + T4 + T5 + T6 + T7 + T8 + T9 + T10 + T11;
  Chk = Chk / 89;
  if (Chk === Math.floor(Chk)) {
    return true;
  }

  return false;
};

export const PageTitle = ({
  title = "",
  onBack,
  displayBackIcon,
  style = {},
}) => {
  const { isDarkMode } = useTheme();
  const theme = getAppTheme(isDarkMode);
  return (
    <Box sx={{ mt: 4, display: displayBackIcon ? "flex" : "block" }}>
      {displayBackIcon && (
        <Box
          sx={{ cursor: "pointer", mr: "10px" }}
          onClick={() => onBack()}
          data-testid="create-business-account--Box-0">
          <ArrowBack color={theme.palette.primary.main} />
        </Box>
      )}
      <Typography
        sx={{
          ...commonThemes.pageTitle,
          color: isDarkMode ? "#FFF" : "rgb(73, 85, 112)",
          marginBottom: "40px",
          ...style,
        }}
      >
        {title}
      </Typography>
    </Box>
  );
};

const CreateBusinessAccount = ({ isCreatingSubscriber }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const theme = getAppTheme(isDarkMode);

  const [isLoading, setIsLoading] = useState(false);
  const [submitSucceeded, setSubmitSucceeded] = useState(false);
  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const [isAbnOpen, setIsAbnOpen] = useState(false);
  const [abnApiResult, setAbnApiResult] = useState(null);
  const [abnResultsAccepted, setAbnResultsAccepted] = useState(null);
  const [selectedBusinessType, setSelectedBusinessType] = useState(null);

  const {
    data: businessActivities,
    error: businessActivitiesError,
    loading: businessActivitiesLoading,
  } = useApiCallDefault(
    `https://${envConfig.apiDev1Exacc}/api/v1/en-au/business-details/get-business-main-activities-async`,
    "GET",
    default_headers,
    null,
    navigate
  );

  const {
    data: subscribersData,
    error: subscribersError,
    loading: subscribersLoading,
  } = useApiCall({
    url: `https://${
      envConfig.apiDev1
    }/api/v3/en-au/subscribers/get-selection-list?LoginEmail=${getCookie(
      "loginEmail"
    )}&BaseHostURL=${envConfig.loginUrl}`,
    method: "GET",
    headers: {},
    navigate,
    skip: isCreatingSubscriber,
  });

  const {
    data: businessTypesData,
    error: businessTypesError,
    loading: businessTypesLoading,
  } = useApiCallDefault(
    `https://${envConfig.apiDev1}/api/v3/en-au/businesses/get-types?BaseHostURL=${envConfig.loginUrl}`,
    "GET",
    default_headers,
    null,
    navigate
  );

  const formSchema = Yup.object().shape({
    ownership: Yup.string().required("Ownership selection is required"),
    subscriberAccount: isCreatingSubscriber
      ? Yup.object().nullable()
      : Yup.object()
          .nullable()
          .required("Selecting a subscriber is required")
          .test(
            "subscriber-valid",
            "Selecting a subscriber is required",
            (value) => {
              return (
                value != null && value.xxsName != null && value.xxsName !== ""
              );
            }
          ),
    businessActivity: Yup.object().nullable(),
    businessName: Yup.string().required("Business Name is required"),
    companyRef: Yup.string()
      .nullable()
      .max(7, "Short code must be maximum 7 characters")
      .matches(
        /^[a-zA-Z0-9\-_.!@&]*$/,
        "Short code can only contain letters, numbers, and - _ . ! @ & characters"
      ),
    abn: Yup.string()
      .nullable()
      .test("valid-abn", "Invalid ABN", (value) =>
        AustralianBusinessNumber(value)
      ),
    businessOwnerEmail: Yup.string()
      .nullable()
      .when("ownership", {
        is: (val) => {
          return val === "owner";
        },
        then: () => Yup.string().nullable(),
        otherwise: () =>
          Yup.string()
            .email("Invalid email")
            .required("Business Owner Email is required"),
      }),
    databaseOwnerFirstName: Yup.string().when("ownership", {
      is: "someoneElse",
      then: (schema) => schema.required("Business owner's first name is required"),
      otherwise: (schema) => schema.nullable(),
    }),
    databaseOwnerLastName: Yup.string().when("ownership", {
      is: "someoneElse",
      then: (schema) => schema.required("Business owner's last name is required"),
      otherwise: (schema) => schema.nullable(),
    }),
    databaseOwnerMobilePhone: Yup.string().nullable(),
    businessType: Yup.string().nullable().required("Business Type is required"),
    gstRegistered: Yup.boolean(),
    countryOfResidence: Yup.object().nullable().shape({
      ctrID: Yup.string().nullable(),
      ctrCode: Yup.string().nullable(),
      ctrName: Yup.string().nullable(),
    }),
    allowTeamAccess: Yup.boolean(),
    allowTeamInviteExternal: Yup.boolean(),
    agentTypeBasTaxBT: Yup.string()
      .nullable()
      .oneOf(["", "T", "B", null]),
    registeredAgentNumber: Yup.string().nullable(),
    registeredAgentName: Yup.string().nullable(),
    registeredAgentEmailAddress: Yup.string().nullable().email("Invalid email"),
    registeredAgentPhoneNumber: Yup.string().nullable(),
  });

  const formik = useFormik({
    initialValues: {
      ownership: "owner", // Added ownership field with default 'owner'
      subscriberAccount: null,
      businessActivity: null,
      businessName: "",
      companyRef: "",
      abn: "",
      businessOwnerEmail: "",
      databaseOwnerFirstName: "",
      databaseOwnerLastName: "",
      databaseOwnerMobilePhone: "",
      contactName: "",
      contactEmail: "",
      contactPhone: "",
      manageBusiness: false,
      businessType: null,
      gstRegistered: false,
      countryOfResidence: { ctrID: 14, ctrCode: "AUS", ctrName: "Australia" },
      allowTeamAccess: false,
      allowTeamInviteExternal: false,
      agentTypeBasTaxBT: "",
      registeredAgentNumber: "",
      registeredAgentName: "",
      registeredAgentEmailAddress: "",
      registeredAgentPhoneNumber: "",
    },

    validationSchema: formSchema,
    onSubmit: async (values) => {
      if (!executeRecaptcha) return;
      setIsLoading(true);
      let recaptchaToken;
      try {
        recaptchaToken = await executeRecaptcha("create_business_file");
      } catch (err) {
        enqueueSnackbar("reCAPTCHA verification failed. Please try again.", { variant: "error" });
        setIsLoading(false);
        return;
      }
      // Determine the Business Owner Email based on ownership selection
      const businessOwnerEmail =
        values.ownership === "owner"
          ? getCookie("loginEmail")
          : values.businessOwnerEmail;
      const databaseOwnerContact =
        values.ownership === "someoneElse"
          ? {
              DatabaseOwnerFirstName: values.databaseOwnerFirstName.trim(),
              DatabaseOwnerLastName: values.databaseOwnerLastName.trim(),
              DatabaseOwnerMobilePhone: values.databaseOwnerMobilePhone.trim(),
            }
          : {
              DatabaseOwnerFirstName: "",
              DatabaseOwnerLastName: "",
              DatabaseOwnerMobilePhone: "",
            };
      const apiData = isCreatingSubscriber
        ? {
            DatabaseSubscriberID: values.subscriberAccount?.xxsId,
            BusinessName: values.businessName,
            CompanyRef:
              values.companyRef || values.businessName.substring(0, 7),
            BusinessTypeId: values.businessType,
            BusinessRegistered4GSTYN: values.gstRegistered ? "Y" : "N",
            AutoAccessYN: values.allowTeamAccess ? "Y" : "N",
            AllowTeamMembersInviteExternalUsersYN: "N",
            // CountryId: values.countryOfResidence.ctrID,
            CountryId: 14, // australia
            TaxCode: values.abn.replace(/\s+/g, ""),
            ContactName: values.contactName,
            ContactPhoneNo: values.contactPhone,
            OwnerEmail: businessOwnerEmail, // Updated to use the determined email
            ...buildSubscriberAgentPayload(values),
            DemoYN: "N",
            DefBASproject: getDefaultFyProjectDate(),
            DefTrProject: getDefaultFyProjectDate(),
            ...databaseOwnerContact,
          }
        : {
            // CountryID: values.countryOfResidence.ctrID,
            CountryID: 14, // Australia
            BusinessTypeID: values.businessType,
            CompanyName: values.businessName,
            CompanyRef:
              values.companyRef || values.businessName.substring(0, 7),
            DatabaseOwnerEmail: businessOwnerEmail, // Updated to use the determined email
            CreatedByGlobalUserID: getCookie("globalUserId"),
            DatabaseSubscriberID: values.subscriberAccount?.xxsId,
            CompanyTaxCode: values?.abn?.replace(/\s+/g, ""),
            BusinessActivityCode: values.businessActivity?.Value,
            BusinessRegistered4GSTYN: values.gstRegistered ? "Y" : "N",
            BillingSubscriderID: getCookie("globalUserId"),
            CurrentSubscriberIsAdminYN: "Y",
            CurrentSubscriberAutoAddYN: values.allowTeamAccess ? "Y" : "N",
            CurrentSubscriberCanAddOthersYN: "N",
            IsDemoFile: !!location?.state?.isDemo,
            ...databaseOwnerContact,
          };
      console.log("5");
      try {
        let url;
        if (isCreatingSubscriber) {
          url = `https://${envConfig.apiDev1}/api/v6/en-au/subscribers/create-subscriber-with-business-file-async?BaseHostURL=${envConfig.loginUrl}`;
        } else {
          url = `https://${envConfig.apiDev1}/api/v8/en-au/databases/create-new-business-file-async?BaseHostURL=${envConfig.loginUrl}`;
        }

        const headers = {
          ...getHeaders(),
        };
        headers.captchav3 = envConfig.loginUrl + ":" + recaptchaToken;

        const response = await fetch(url, {
          method: "POST",
          headers,
          body: JSON.stringify(apiData),
        });
        const responseData = await response.json();
        if (!response.ok) {
          setIsLoading(false);
          checkAndUpdateApiKeyExpiration();
          return;
        }

        if (responseData.SuccessYN === "N") {
          setIsLoading(false);
          return;
        } else if (responseData.SuccessYN === "Y") {
          setSubmitSucceeded(true);
          enqueueSnackbar(
            isCreatingSubscriber
              ? "New subscriber created!"
              : "New exacc file created! You will be redirected",
            { variant: "success" }
          );

          if (isCreatingSubscriber) {
            if (responseData.BusinessFileId) {
              setCrossDomainCookie("databaseId", responseData.BusinessFileId);
              await createSessionForExternal(
                `https://${envConfig.mainServiceUrl}/${responseData.BusinessFileId}/dashboard`
              );
            } else {
              navigate(`/subscriber/${responseData.SubscriberId}`);
            }
          } else {
            setCrossDomainCookie("databaseId", responseData.NewBusinessFileId);
            await createSessionForExternal(
              `https://${envConfig.mainServiceUrl}/${responseData.NewBusinessFileId}/dashboard`
            );
          }
          return;
        }
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
      }
    },
  });

  const handleCancel = () => {
    if (formik.dirty) {
      setOpenCancelDialog(true);
    } else {
      handleCancelConfirm();
    }
  };

  const handleContinueEditing = () => {
    setOpenCancelDialog(false);
  };

  const handleCancelConfirm = () => {
    setOpenCancelDialog(false);
    if (location?.state?.redirectBackTo) {
      navigate(location.state.redirectBackTo);
    } else {
      navigate("/user-dashboard");
    }
  };

  const handleAbnChange = (event) => {
    let value = event.target.value;
    const numericValue = value.replace(/\D/g, "");
    formik.setFieldValue("abn", numericValue);
  };

  // Add the handleAbnPaste function
  const handleAbnPaste = (event) => {
    event.preventDefault(); // Prevent the default paste action
    let paste = (event.clipboardData || window.clipboardData).getData("text");
    paste = paste.replace(/[\s-]/g, "");
    formik.setFieldValue("abn", paste);
  };

  useEffect(() => {
    if (abnResultsAccepted) {
      const newValues = {
        ...formik.values,
        businessName: abnResultsAccepted.EntityName,
        gstRegistered: Boolean(abnResultsAccepted.GSTRegistered === "Yes"),
        businessType: abnResultsAccepted.EntityLegalForm_Enum8,
      };

      formik.setValues(newValues);
      setAbnResultsAccepted(null);
    }
  }, [abnResultsAccepted]);

  useEffect(() => {
    setSelectedBusinessType(
      businessTypesData?.ListOfBusinessTypes?.find(
        (type) => type?.xxtID === formik?.values?.businessType
      )
    );
  }, [formik?.values?.businessType, businessTypesData]);

  useEffect(() => {
    if (subscribersLoading || isCreatingSubscriber) return;
    if (subscribersData?.ListOfSubscribers?.length) return;
    navigate("/create-new-subscriber", {
      navigateToMainService: true,
    });
  });

  const nameOfSubscriber = formik?.values?.subscriberAccount?.xxsName
    ? formik.values.subscriberAccount.xxsName
    : "the Subscriber";
  const showRegisteredAgentDetails = shouldShowRegisteredAgentDetails(
    formik.values.agentTypeBasTaxBT
  );

  return (
    <>
      <Dialog
        open={isAbnOpen}
        onClose={() => setIsAbnOpen(false)}
        data-testid="create-business-account--Dialog-0">
        <LookupAbn
          businessName={formik.values.businessName}
          abnApiResult={abnApiResult}
          setAbnApiResult={setAbnApiResult}
          abn={formik.values.abn}
          setIsAbnOpen={setIsAbnOpen}
          setAbnResultsAccepted={setAbnResultsAccepted}
        />
      </Dialog>
      <Dialog
        open={openCancelDialog}
        onClose={() => setOpenCancelDialog(false)}
        data-testid="create-business-account--Dialog-1">
        <DialogContent data-testid="create-business-account--DialogContent-0">
          <Typography
            sx={{
              fontSize: "24px",
              fontWeight: 400,
              lineHeight: "150%",
              letterSpacing: "-0.264px",
              mb: 5,
            }}
          >
            Cancel creating Business Account?
          </Typography>
          <Typography
            sx={{
              color: theme.typography.linkColor,
              fontSize: "16px",
              fontWeight: 400,
              lineHeight: "150%",
              letterSpacing: "-0.176px",
            }}
          >
            Do you want to cancel creating Business Account?
          </Typography>
          <Typography
            sx={{
              color: theme.typography.linkColor,
              fontSize: "16px",
              fontWeight: 400,
              lineHeight: "150%",
              letterSpacing: "-0.176px",
            }}
          >
            Your data will be lost.
          </Typography>
          <Box sx={{ mt: 5, textAlign: "right" }}>
            <Button
              sx={{
                background: "transparent",
                mr: "9px",
                "&:hover": {
                  background: "transparent",
                },
              }}
              disableRipple
              variant="outlined"
              onClick={handleContinueEditing}
              data-testid="create-business-account--Button-0">
              Continue Editing
            </Button>
            <Button
              disableRipple
              variant="contained"
              color="primary"
              sx={
                isDarkMode
                  ? {
                      "&:hover": { color: "#FFF" },
                    }
                  : {}
              }
              onClick={handleCancelConfirm}
              data-testid="create-business-account--Button-1">
              Cancel
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
      <Box
        sx={{
          borderRadius: "4px",
          mb: "74px",
        }}
      >
        <PageTitle
          title={
            isCreatingSubscriber
              ? "Create Subscriber Account and your own Exacc File. This will allow you to manage your Team and add additional Exacc Files."
              : location?.state?.isDemo
              ? "Create new Demo Exacc File"
              : "Create new Exacc Business File"
          }
          style={isCreatingSubscriber ? { fontSize: "20px" } : {}}
          onBack={() => handleCancel()}
          displayBackIcon={true}
        />

        <Box
          sx={{
            boxShadow: "rgba(69, 90, 100, 0.1) 0px 0px 10px 0px",
            border: theme.palette.borders.main,
            px: 5,
            pt: 6,
            pb: 10.25,
            borderRadius: "8px",
          }}
        >
          <form onSubmit={formik.handleSubmit}>
            {!isCreatingSubscriber && (
              <Autocomplete
                sx={{
                  mt: 3,
                  maxWidth: "480px",
                  "p.Mui-error": {
                    position: "absolute",
                    bottom: -25,
                  },
                }}
                autoSelect={true}
                autoHighlight={true}
                required={Boolean(!isCreatingSubscriber)}
                options={subscribersData?.ListOfSubscribers || []}
                getOptionLabel={(option) => option?.xxsName || ""}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    label="Subscriber account"
                    variant="outlined"
                    fullWidth
                    error={
                      formik.touched.subscriberAccount &&
                      Boolean(formik.errors.subscriberAccount?.xxsName)
                    }
                    helperText={
                      formik.touched.subscriberAccount &&
                      formik.errors.subscriberAccount?.xxsName
                    }
                    data-testid="create-business-account--TextField-0" />
                )}
                // Ensure value is null if undefined
                value={formik.values.subscriberAccount || null}
                onChange={(event, value) => {
                  formik.setFieldValue("subscriberAccount", value || null);
                }}
                data-testid="create-business-account--Autocomplete-0" />
            )}

            {/* Business Name textbox required*/}
            <TextField
              sx={{
                mt: 4,
                maxWidth: "480px",
                display: "block",
                "p.Mui-error": {
                  position: "absolute",
                },
              }}
              label="Business Name"
              variant="outlined"
              required
              fullWidth
              {...formik.getFieldProps("businessName")}
              error={
                formik.touched.businessName &&
                Boolean(formik.errors.businessName)
              }
              helperText={
                formik.touched.businessName && formik.errors.businessName
              }
              data-testid="create-business-account--TextField-1" />

            {/* Short code (CompanyRef) field - only show for non-demo files */}
            {!location?.state?.isDemo && (
              <Box sx={{ display: "flex", alignItems: "center", mt: 4 }}>
                <TextField
                  sx={{
                    maxWidth: "480px",
                    display: "block",
                    "p.Mui-error": {
                      position: "absolute",
                    },
                  }}
                  label="Short code"
                  variant="outlined"
                  fullWidth
                  inputProps={{ maxLength: 7 }}
                  {...formik.getFieldProps("companyRef")}
                  error={
                    formik.touched.companyRef &&
                    Boolean(formik.errors.companyRef)
                  }
                  helperText={
                    formik.touched.companyRef && formik.errors.companyRef
                  }
                  data-testid="create-business-account--TextField-2" />
                <Box sx={{ ml: 1 }}>
                  <HelpTooltip
                    tooltipContent={
                      <Box sx={{ padding: "4px 8px" }}>
                        <Typography
                          sx={{
                            fontWeight: 400,
                            fontSize: "14px",
                            color: "#ffffff",
                          }}
                        >
                          Short Code will appear on browser page title and on
                          all pages, so you can identify the business, helpful
                          if you work on multiple files at once. Max length: 7
                          characters. If you leave empty, first 7 characters of
                          Business Name will be taken.
                        </Typography>
                      </Box>
                    }
                  />
                </Box>
              </Box>
            )}

            <Box sx={{ display: "flex", alignItems: "center" }}>
              {/* ABN textbox required */}
              <TextField
                required={!!isCreatingSubscriber}
                sx={{
                  mt: 4,
                  maxWidth: "320px",
                  display: "block",
                  "p.Mui-error": {
                    position: "absolute",
                  },
                }}
                label="ABN"
                variant="outlined"
                fullWidth
                {...formik.getFieldProps("abn")}
                error={formik.touched.abn && Boolean(formik.errors.abn)}
                helperText={formik.touched.abn && formik.errors.abn}
                onChange={handleAbnChange}
                onPaste={handleAbnPaste}
                data-testid="create-business-account--TextField-3" />
              <Link
                className="link"
                style={{
                  marginTop: "30px",
                  marginLeft: "16px",
                  paddingLeft: "8px",
                  paddingRight: "8px",
                  color: theme.palette.primary.main,
                  textDecoration: "none",
                  fontSize: "14px",
                  fontWeight: 700,
                  lineHeight: "24px",
                  letterSpacing: "0.4px",
                }}
                to="#"
                onClick={(e) => {
                  if (
                    !formik.values.abn ||
                    !AustralianBusinessNumber(formik.values.abn)
                  ) {
                    if (formik.values.businessName) {
                      window.open(
                        `https://abn.business.gov.au/Search/ResultsActive?SearchText=${formik.values.businessName.replace(
                          / /g,
                          "_"
                        )}`,
                        "_blank"
                      );
                      return;
                    }
                    if (formik.values.abn) {
                      enqueueSnackbar("Enter a valid ABN", {
                        variant: "warning",
                      });
                      return;
                    } else {
                      enqueueSnackbar("Enter Business Name or ABN first", {
                        variant: "warning",
                      });
                      return;
                    }
                  }
                  e.preventDefault();
                  setIsAbnOpen(true);
                }}
                data-testid="create-business-account--Link-0">
                Lookup ABN
              </Link>
            </Box>

            {/* Ownership Radio Buttons */}
            <Box sx={{ display: "flex", alignItems: "center", mt: 4 }}>
              <FormControl component="fieldset" data-testid="create-business-account--FormControl-0">
                <RadioGroup
                  row
                  aria-label="ownership"
                  name="ownership"
                  value={formik.values.ownership}
                  onChange={formik.handleChange}
                  data-testid="create-business-account--RadioGroup-0">
                  <FormControlLabel
                    value="owner"
                    control={<Radio data-testid="create-business-account--Radio-0" />}
                    label="I am the owner"
                    data-testid="create-business-account--FormControlLabel-0" />
                  <FormControlLabel
                    value="someoneElse"
                    control={<Radio data-testid="create-business-account--Radio-1" />}
                    label="Someone else owns this business"
                    data-testid="create-business-account--FormControlLabel-1" />
                </RadioGroup>
              </FormControl>
              <Box sx={{ ml: 1 }}>
                <HelpTooltip
                  tooltipContent={
                    <Box sx={{ padding: "4px 8px" }}>
                      <Typography
                        sx={{
                          fontWeight: 400,
                          fontSize: "14px",
                          color: "#ffffff",
                        }}
                      >
                        If you're not the Business Owner, select this option and
                        provide their name, email address, and optionally phone
                        number to grant them admin permissions.
                      </Typography>
                    </Box>
                  }
                />
              </Box>
            </Box>

            {formik.values.ownership === "someoneElse" && (
              <Typography
                sx={{
                  color: theme.typography.linkColor,
                  fontSize: "14px",
                  mt: 4,
                  mb: 0,
                }}
              >
                Provide the Business Owner's email and name (phone optional) to
                grant them admin permissions.
              </Typography>
            )}
            {/* Business Owner Email Address - Conditionally Rendered */}
            {formik.values.ownership === "someoneElse" && (
              <Box>
                <TextField
                  sx={{
                    mt: 4,
                    maxWidth: "480px",
                    display: "block",
                    "p.Mui-error": {
                      position: "absolute",
                    },
                  }}
                  label="Business Owner Email Address"
                  variant="outlined"
                  fullWidth
                  required
                  type="email"
                  {...formik.getFieldProps("businessOwnerEmail")}
                  error={
                    formik.touched.businessOwnerEmail &&
                    Boolean(formik.errors.businessOwnerEmail)
                  }
                  helperText={
                    formik.touched.businessOwnerEmail &&
                    formik.errors.businessOwnerEmail
                  }
                  data-testid="create-business-account--TextField-4" />
                <TextField
                  sx={{
                    mt: 4,
                    maxWidth: "480px",
                    display: "block",
                    "p.Mui-error": {
                      position: "absolute",
                    },
                  }}
                  label="Business Owner First Name"
                  variant="outlined"
                  fullWidth
                  required
                  {...formik.getFieldProps("databaseOwnerFirstName")}
                  error={
                    formik.touched.databaseOwnerFirstName &&
                    Boolean(formik.errors.databaseOwnerFirstName)
                  }
                  helperText={
                    formik.touched.databaseOwnerFirstName &&
                    formik.errors.databaseOwnerFirstName
                  }
                  data-testid="create-business-account--TextField-databaseOwnerFirstName"
                />
                <TextField
                  sx={{
                    mt: 4,
                    maxWidth: "480px",
                    display: "block",
                    "p.Mui-error": {
                      position: "absolute",
                    },
                  }}
                  label="Business Owner Last Name"
                  variant="outlined"
                  fullWidth
                  required
                  {...formik.getFieldProps("databaseOwnerLastName")}
                  error={
                    formik.touched.databaseOwnerLastName &&
                    Boolean(formik.errors.databaseOwnerLastName)
                  }
                  helperText={
                    formik.touched.databaseOwnerLastName &&
                    formik.errors.databaseOwnerLastName
                  }
                  data-testid="create-business-account--TextField-databaseOwnerLastName"
                />
                <TextField
                  sx={{
                    mt: 4,
                    maxWidth: "480px",
                    display: "block",
                    "p.Mui-error": {
                      position: "absolute",
                    },
                  }}
                  label="Business Owner Phone Number"
                  variant="outlined"
                  fullWidth
                  {...formik.getFieldProps("databaseOwnerMobilePhone")}
                  error={
                    formik.touched.databaseOwnerMobilePhone &&
                    Boolean(formik.errors.databaseOwnerMobilePhone)
                  }
                  helperText={
                    formik.touched.databaseOwnerMobilePhone &&
                    formik.errors.databaseOwnerMobilePhone
                  }
                  data-testid="create-business-account--TextField-databaseOwnerMobilePhone"
                />
              </Box>
            )}

            {/* If 'someoneElse' is selected, show the label below the email field */}

            {/* Rest of the form remains unchanged */}

            {/* Autocomplete for Business Type */}
            <Autocomplete
              sx={{
                mt: 4,
                mb: 4,
                maxWidth: "480px",
                "p.Mui-error": {
                  position: "absolute",
                  bottom: -25,
                },
              }}
              required
              value={selectedBusinessType || null}
              options={businessTypesData?.ListOfBusinessTypes || []}
              getOptionLabel={(option) => option?.xxtTypeName || ""}
              autoSelect={true}
              autoHighlight={true}
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  label="Business Type"
                  variant="outlined"
                  fullWidth
                  // {...formik.getFieldProps("businessType")}
                  error={
                    formik.touched.businessType &&
                    Boolean(formik.errors.businessType)
                  }
                  helperText={
                    formik.touched.businessType && formik.errors.businessType
                  }
                  data-testid="create-business-account--TextField-5" />
              )}
              onChange={(event, value) => {
                formik.setFieldValue("businessType", value?.xxtID || null);
              }}
              data-testid="create-business-account--Autocomplete-1" />

            {!isCreatingSubscriber && (
              <Autocomplete
                sx={{
                  mt: 3,
                  maxWidth: "480px",
                  "p.Mui-error": {
                    position: "absolute",
                    bottom: -25,
                  },
                }}
                autoSelect={true}
                autoHighlight={true}
                options={businessActivities?.ListOfBusinessActivities || []}
                getOptionLabel={(option) => option?.Display || ""}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Business activity (optional)"
                    variant="outlined"
                    fullWidth
                    data-testid="create-business-account--TextField-6" />
                )}
                value={formik.values.businessActivity || null}
                onChange={(event, value) => {
                  formik.setFieldValue("businessActivity", value || null);
                }}
                data-testid="create-business-account--Autocomplete-2" />
            )}

            {/* GST Registered Checkbox */}
            <Box
              sx={{
                margin: "24px -20px 8px -20px",
                padding: "0 20px 4px 20px",
                boxShadow: "rgba(1, 1, 1, 0.4) 0px 0px 3px",
                borderRadius: "8px",
              }}
            >
              <FormControlLabel
                sx={{
                  mt: isCreatingSubscriber ? "12px" : 1,
                }}
                control={
                  <Checkbox
                    checked={formik.values.gstRegistered}
                    onChange={formik.handleChange}
                    name="gstRegistered"
                    data-testid="create-business-account--Checkbox-0" />
                }
                label="This business is registered for GST"
                data-testid="create-business-account--FormControlLabel-2" />

              {/* Allow Team Access Checkbox */}
              {nameOfSubscriber && !isCreatingSubscriber && (
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <FormControlLabel
                    sx={{ mt: 1 }}
                    control={
                      <Checkbox
                        checked={formik.values.allowTeamAccess}
                        onChange={formik.handleChange}
                        name="allowTeamAccess"
                        data-testid="create-business-account--Checkbox-1" />
                    }
                    label={`Allow ${
                      formik.values.subscriberAccount?.xxsName || "Subscriber"
                    } Team members to access this Business File automatically`}
                    data-testid="create-business-account--FormControlLabel-3" />
                  <Box sx={{ mt: "15.8px" }}>
                    <HelpTooltip
                      tooltipContent={
                        <Box sx={{ padding: "4px 8px" }}>
                          <Typography
                            sx={{
                              fontWeight: 400,
                              fontSize: "14px",
                              color: "#ffffff",
                            }}
                          >
                            Selecting this option will allow your Subscriber
                            Account team members to gain access with Admin
                            privileges without any invitation or further
                            confirmation. They can do it by selecting the
                            company and clicking 'Request access'. If this
                            option is not selected, the same action will send
                            you a request to approve the request.
                          </Typography>
                        </Box>
                      }
                    />
                  </Box>
                </Box>
              )}
            </Box>
            {isCreatingSubscriber && (
              <Box sx={{ mt: "4px" }}>
                <FormControl
                  component="fieldset"
                  data-testid="create-business-account--FormControl-1"
                >
                  <RadioGroup
                    row
                    aria-label="subscriber agent type"
                    name="agentTypeBasTaxBT"
                    value={formik.values.agentTypeBasTaxBT}
                    onChange={formik.handleChange}
                    data-testid="create-business-account--RadioGroup-1">
                    <FormControlLabel
                      value="T"
                      control={<Radio data-testid="create-business-account--Radio-2" />}
                      label="I am a Tax Agent"
                      data-testid="create-business-account--FormControlLabel-4" />
                    <FormControlLabel
                      value="B"
                      control={<Radio data-testid="create-business-account--Radio-3" />}
                      label="I am a BAS Agent"
                      data-testid="create-business-account--FormControlLabel-5" />
                    <FormControlLabel
                      value=""
                      control={<Radio data-testid="create-business-account--Radio-4" />}
                      label="None"
                      data-testid="create-business-account--FormControlLabel-6" />
                  </RadioGroup>
                </FormControl>
              </Box>
            )}
            {isCreatingSubscriber && (
              <Typography
                sx={{
                  color: isDarkMode ? "#FFF" : "#252525",
                  mt: 1,
                  fontWeight: 300,
                  lineHeight: "175%",
                  letterSpacing: "0.15px",
                  mb: 2,
                }}
              >
                You will be able to lodge various reports (e.g. TPAR, STP) as
                Tax Agent, skipping registration of the software with the ATO
                per each business. This ability will be passed to Members of
                this subscription as a permission (next step). You can add these
                details later.
              </Typography>
            )}
            {showRegisteredAgentDetails && (
              <>
                <TextField
                  sx={{
                    mt: 2,
                    maxWidth: "480px",
                    display: "block",
                  }}
                  label="Registered Agent Number"
                  variant="outlined"
                  fullWidth
                  type="text"
                  {...formik.getFieldProps("registeredAgentNumber")}
                  data-testid="create-business-account--TextField-7" />

                {/* Contact Phone Number, not required */}
                <TextField
                  sx={{
                    mt: 4,
                    maxWidth: "480px",
                  }}
                  label="Registered Agent Email Address"
                  variant="outlined"
                  fullWidth
                  {...formik.getFieldProps("registeredAgentEmailAddress")}
                  data-testid="create-business-account--TextField-9" />

                <TextField
                  sx={{
                    mt: 4,
                    maxWidth: "480px",
                    display: "block",
                  }}
                  label="Registered Agent Phone Number"
                  variant="outlined"
                  fullWidth
                  type="text"
                  {...formik.getFieldProps("registeredAgentPhoneNumber")}
                  data-testid="create-business-account--TextField-10" />
              </>
            )}
            {/* reCAPTCHA v3 runs invisibly on submit */}
            <Box sx={{ textAlign: "left", mt: 2 }}>
              <Button
                sx={{
                  background: "transparent",
                  mr: "9px",
                  "&:hover": {
                    background: "transparent",
                  },
                }}
                disableRipple
                variant="outlined"
                onClick={handleCancel}
                data-testid="create-business-account--Button-2">
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isLoading || submitSucceeded}
                sx={{ ml: 2, minHeight: "36.5px", minWidth: "77px" }}
                data-testid="create-business-account--Button-3">
                {isLoading ? (
                  <CircularProgress
                    size={24}
                    sx={{
                      color: (theme) => theme.palette.primary.contrastText,
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      marginTop: "-12px",
                      marginLeft: "-12px",
                    }}
                  />
                ) : (
                  "Create"
                )}
              </Button>
            </Box>
          </form>
        </Box>
      </Box>
    </>
  );
};

export default CreateBusinessAccount;
